import { type ComputedRef, computed, ref } from 'vue';
import type { GridLayout } from '../../../shared/lib/pick-grid-layout';
import type {
  ImageGridFit,
  ImageGridFlow,
  ImageGridItem,
  ImageGridPageItem,
  ImageGridSizing,
} from './types';

const EMPTY_PIXEL =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

interface ImageGridLayoutOptions {
  items: ImageGridItem[];
  layout: GridLayout;
  flow?: ImageGridFlow;
  sizing: ImageGridSizing;
  cellWidth: number | string;
  cellHeight: number | string;
  cellAspectRatio: number;
  fit: ImageGridFit;
  gap: string;
  cellRadius: string;
  snap: boolean;
  selectedUuids: string[];
  stateClassPrefix: string;
  arrowScale: number;
  checkmarkSize: string;
  itemScale?: (item: ImageGridItem) => number;
  itemRatioKnown?: (item: ImageGridItem) => boolean;
}

interface ImageGridLayout {
  cellStyle: (item: ImageGridItem) => Record<string, string>;
  imageSrc: (item: ImageGridItem) => string;
  isSelected: (item: ImageGridItem) => boolean;
  itemClass: (item: ImageGridItem) => Record<string, boolean>;
  listStyle: ComputedRef<Record<string, string>>;
  pages: ComputedRef<ImageGridPageItem[][]>;
  pageStyle: (page: ImageGridPageItem[]) => Record<string, string>;
  rootStyle: ComputedRef<Record<string, string>>;
  scrollableStyle: ComputedRef<Record<string, string>>;
  setViewportSize: (width: number, height: number) => void;
}

// A page's width in row heights: every cell is one row tall and its ratio
// wide, a column is as wide as its widest cell, and the page is the sum of
// its columns. Empty columns (a partial last page) cost nothing.
export function pageColumnWidths(
  ratios: number[],
  layout: GridLayout,
  flow: ImageGridFlow
): number[] {
  const columns = Math.max(1, layout.columns);
  const rows = Math.max(1, layout.rows);
  const widths = new Array<number>(columns).fill(0);
  ratios.forEach((ratio, index) => {
    const column =
      flow === 'column' ? Math.floor(index / rows) : index % columns;
    if (column < columns) {
      widths[column] = Math.max(widths[column] ?? 0, ratio);
    }
  });
  return widths.filter((width) => width > 0);
}

// A page's height in row heights: a row is as tall as its largest cell, and
// a row that a partial page leaves empty keeps its plain height. A page of
// plain cells is thus `rows` units tall.
export function pageRowUnits(
  scales: number[],
  layout: GridLayout,
  flow: ImageGridFlow
): number {
  const columns = Math.max(1, layout.columns);
  const rows = Math.max(1, layout.rows);
  const heights = new Array<number>(rows).fill(0);
  scales.forEach((scale, index) => {
    const row = flow === 'column' ? index % rows : Math.floor(index / columns);
    if (row < rows) {
      heights[row] = Math.max(heights[row] ?? 0, scale);
    }
  });
  return heights.reduce((sum, height) => sum + (height || 1), 0);
}

export function useImageGridLayout(
  options: ImageGridLayoutOptions
): ImageGridLayout {
  const viewportWidth = ref(0);
  const viewportHeight = ref(0);
  const pageSize = computed(() =>
    Math.max(1, options.layout.columns * options.layout.rows)
  );
  const pages = computed(() => {
    const result: ImageGridPageItem[][] = [];
    for (let index = 0; index < options.items.length; index += pageSize.value) {
      result.push(
        options.items
          .slice(index, index + pageSize.value)
          .map((item, offset) => ({ item, index: index + offset }))
      );
    }
    return result;
  });
  const selectedUuidSet = computed(() => new Set(options.selectedUuids));
  const gapPx = computed(() => cssLengthToPx(options.gap));
  const defaultRatio = computed(() => options.cellAspectRatio || 1);

  // The row height a full page of default-ratio cells gets. It sizes the
  // arrows and is the fallback when the viewport is not measured yet.
  const baseRowHeight = computed(() => {
    if (options.sizing === 'width-fixed') {
      return `calc(${cssLength(options.cellWidth)} / ${defaultRatio.value})`;
    }
    if (options.sizing === 'height-fixed') {
      return cssLength(options.cellHeight);
    }
    const columns = Math.max(1, options.layout.columns);
    return `${fillRowHeightPx(new Array<number>(columns).fill(defaultRatio.value))}px`;
  });

  const listStyle = computed(() => ({ gap: options.gap }));
  const scrollableStyle = computed(() => ({
    scrollSnapType: options.snap ? 'x mandatory' : 'none',
  }));
  const rootStyle = computed(() => ({
    '--ImageGrid--arrow-size': `calc(${baseRowHeight.value} * ${options.arrowScale})`,
  }));

  function setViewportSize(width: number, height: number): void {
    viewportWidth.value = width;
    viewportHeight.value = height;
  }

  // The url a cell shows, or null while it holds the placeholder.
  function ownImageUrl(item: ImageGridItem): string | null {
    return item.isPreloaded !== false && item.url ? item.url : null;
  }

  // Only a cell with its own image on screen takes its own ratio. An item
  // carries a guessed ratio until then, and a guess wide of the real shape
  // would resize the whole page and snap it back when the image arrives.
  function itemRatio(item: ImageGridItem): number {
    const trusted = ownImageUrl(item) || options.itemRatioKnown?.(item);
    const ratio = trusted ? item.aspectRatio : null;
    return ratio || defaultRatio.value;
  }

  function itemScale(item: ImageGridItem): number {
    const scale = options.itemScale?.(item) ?? 1;
    return Number.isFinite(scale) && scale > 0 ? scale : 1;
  }

  // `fill` shares the viewport out in pixels: the page's rows get the height
  // and its columns the width, whichever binds first.
  function fillRowHeightPx(
    columnWidths: number[],
    rowUnits = Math.max(1, options.layout.rows)
  ): number {
    const rows = Math.max(1, options.layout.rows);
    const width = viewportWidth.value;
    const height = viewportHeight.value;
    if (!(width > 0 && height > 0)) {
      return cssLengthToPx(options.cellHeight);
    }
    const units = columnWidths.reduce((sum, ratio) => sum + ratio, 0) || 1;
    const gaps = Math.max(0, columnWidths.length - 1) * gapPx.value;
    const heightBound = (height - (rows - 1) * gapPx.value) / rowUnits;
    const widthBound = (width - gaps) / units;
    return Math.max(1, Math.min(heightBound, widthBound));
  }

  // The cells of a page share one row height: the preset's own height, or
  // less when the cells' real ratios would run past the page's width. So a
  // wider-than-expected portrait shrinks its page a little instead of being
  // cropped, and a narrower one leaves room that the gaps take up. A page
  // with a scaled cell is no taller than a page of plain cells, thus its
  // row height is less by the scale of its rows.
  function rowHeight(columnWidths: number[], rowUnits: number): string {
    if (options.sizing === 'fill') {
      return `${fillRowHeightPx(columnWidths, rowUnits)}px`;
    }
    const rows = Math.max(1, options.layout.rows);
    const units = columnWidths.reduce((sum, ratio) => sum + ratio, 0) || 1;
    const gaps = Math.max(0, columnWidths.length - 1);
    const byHeight =
      rowUnits === rows
        ? baseRowHeight.value
        : `calc(${baseRowHeight.value} * ${rows / rowUnits})`;
    const byWidth = `calc((100cqw - ${gaps} * ${cssLength(options.gap)}) / ${units})`;
    return `min(${byHeight}, ${byWidth})`;
  }

  function pageStyle(page: ImageGridPageItem[]): Record<string, string> {
    const flow = options.flow ?? 'row';
    const scales = page.map(({ item }) => itemScale(item));
    const columnWidths = pageColumnWidths(
      page.map(({ item }, index) => itemRatio(item) * (scales[index] ?? 1)),
      options.layout,
      flow
    );
    const scaled = scales.some((scale) => scale !== 1);
    // A full page of two or more columns spreads any width its cells leave
    // over into the gaps; a partial page, or a single column, sits in the
    // middle as before. A page with a scaled cell is narrower than a plain
    // page, and wide gaps would push its cells apart: it sits in the middle
    // with the usual gaps.
    const spread =
      !scaled && page.length >= pageSize.value && columnWidths.length > 1;
    // The row height is declared on the page but resolved by the cells: its
    // `cqw` measures the nearest query container, which for a cell is the
    // page and for the page itself would be an ancestor (or the viewport).
    // So the tracks are `auto` and take their height from the cells.
    return {
      '--ImageGrid--row-height': rowHeight(
        columnWidths,
        pageRowUnits(scales, options.layout, flow)
      ),
      gridAutoFlow: options.flow === 'column' ? 'column' : 'row',
      gridTemplateColumns: `repeat(${options.layout.columns}, max-content)`,
      gridTemplateRows: `repeat(${options.layout.rows}, auto)`,
      gap: options.gap,
      justifyContent: spread ? 'space-between' : 'center',
      ...(scaled ? { alignItems: 'center', justifyItems: 'center' } : {}),
      scrollSnapAlign: options.snap ? 'start' : 'none',
    };
  }

  function cssLength(value: number | string): string {
    return typeof value === 'number' ? `${value}rem` : value.trim();
  }

  function cssLengthToPx(value: number | string): number {
    if (typeof value === 'number') {
      return value * rootFontSize();
    }
    const trimmed = value.trim();
    if (trimmed.endsWith('rem')) {
      return Number.parseFloat(trimmed) * rootFontSize();
    }
    if (trimmed.endsWith('px')) {
      return Number.parseFloat(trimmed);
    }
    const numeric = Number.parseFloat(trimmed);
    return Number.isFinite(numeric) ? numeric : 0;
  }

  function rootFontSize(): number {
    if (typeof window === 'undefined') {
      return 16;
    }
    const fontSize = Number.parseFloat(
      getComputedStyle(document.documentElement).fontSize
    );
    return Number.isFinite(fontSize) ? fontSize : 16;
  }

  // A cell is one row tall and its own ratio wide, so nothing is cropped
  // once the image's real ratio is known; until then it holds the preset
  // ratio and takes its final shape on load. A scaled cell is its scale
  // times taller and wider.
  function cellStyle(item: ImageGridItem): Record<string, string> {
    const scale = itemScale(item);
    const row = 'var(--ImageGrid--row-height)';
    const height = scale === 1 ? row : `calc(${row} * ${scale})`;
    const width = `calc(${row} * ${itemRatio(item) * scale})`;
    return {
      '--ImageGrid--item-width': width,
      '--ImageGrid--item-height': height,
      '--ImageGrid--item-radius': options.cellRadius,
      '--ImageGrid--fit': options.fit,
      '--ImageGallery--item-width': width,
      '--ImageGallery--item-height': height,
      '--ImageGallery--item-gap': options.gap,
      '--ImageGallery--item-radius': options.cellRadius,
      '--ImageGallery--text-size': `calc(${width} * 0.06)`,
      '--ImageGrid--checkmark-size':
        options.checkmarkSize ||
        'calc(var(--ImageGallery--text-size) * var(--gallery-checkmark-size-factor, 2))',
      width,
      height,
    };
  }

  function imageSrc(item: ImageGridItem): string {
    return ownImageUrl(item) ?? EMPTY_PIXEL;
  }

  function isSelected(item: ImageGridItem): boolean {
    return selectedUuidSet.value.has(item.uuid);
  }

  function itemClass(item: ImageGridItem): Record<string, boolean> {
    const selected = isSelected(item);
    const loading = item.isPreloaded === false && !item.isError;
    const error = item.isError === true;
    const classes: Record<string, boolean> = {
      'ImageGrid__item--selected': selected,
      'ImageGrid__item--loading': loading,
      'ImageGrid__item--error': error,
    };

    if (options.stateClassPrefix) {
      classes[`${options.stateClassPrefix}__item--selected`] = selected;
      classes[`${options.stateClassPrefix}__item--loading`] = loading;
      classes[`${options.stateClassPrefix}__item--error`] = error;
    }

    return classes;
  }

  return {
    cellStyle,
    imageSrc,
    isSelected,
    itemClass,
    listStyle,
    pages,
    pageStyle,
    rootStyle,
    scrollableStyle,
    setViewportSize,
  };
}
